import { useState, useMemo } from 'react';
import moment from "moment-timezone";
import { useNavigate } from 'react-router-dom';
import { Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/ui/DataTable/index';
import { DataTableColumn } from '@/components/ui/DataTable/types';
import { RoutePath } from '@/section/routerPaths';
import { cn, CommonDestinationTimezone } from '@/lib/utils';
import { AttachCustomerModal } from './AttachCustomerModal';
import {
  TtOrderViewResult,
  TtOrderFilter,
  TtOrderRow,
  TtFillEventRow,
  TtFillEventsFilter,
  getFilteredOrders,
  getFilteredFillEvents,
} from '../transform';

type ViewMode = 'orders' | 'fill_events';

interface TTSnapshotOrdersTableProps {
  orderView: TtOrderViewResult;
  fillEvents: TtFillEventRow[];
  activeFilter: TtOrderFilter;
  onFilterChange: (filter: TtOrderFilter) => void;
  isLoading: boolean;
}

export function TTSnapshotOrdersTable({
  orderView,
  fillEvents,
  activeFilter,
  onFilterChange,
  isLoading,
}: TTSnapshotOrdersTableProps) {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedGuidOrderId, setSelectedGuidOrderId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('orders');
  const [fillEventsFilter, setFillEventsFilter] = useState<TtFillEventsFilter>('all');

  const filteredOrders = useMemo(
    () => getFilteredOrders(orderView, activeFilter),
    [orderView, activeFilter]
  );

    /* const filteredFillEvents = useMemo(
  *   () => getFilteredFillEvents(fillEvents, fillEventsFilter),
  *   [fillEvents, fillEventsFilter]
  * ); */


  const handleRowClick = (order: TtOrderRow) => {
    navigate(
      RoutePath.TTOrdersDetail.replace(':guid_order_id', encodeURIComponent(order.guid_order_id))
    );
  };

  const handleAttachClick = (e: React.MouseEvent, guidOrderId: string) => {
    e.stopPropagation();
    setSelectedGuidOrderId(guidOrderId);
    setModalOpen(true);
  };

  const columns: DataTableColumn<TtOrderRow>[] = useMemo(
    () => [
      {
        id: 'actions',
        header: 'Actions',
        size: 120,
        minSize: 100,
        disableRowClick: true,
        cell: (info) => {
          const order = info.row.original;
          // Only show attach button for orders missing customer ID
          if (order.hasCustomerId) return null;
          return (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => handleAttachClick(e, order.guid_order_id)}
            >
              Attach Info
            </Button>
          );
        },
      },
      {
        id: 'trading_account_name',
        accessorKey: 'trading_account_name',
        header: 'Account',
        size: 180,
        minSize: 120,
        cell: (info) => info.getValue() ?? '-',
      },
      {
        id: 'instrument_name',
        accessorKey: 'instrument_name',
        header: 'Instrument',
        size: 200,
        minSize: 150,
        cell: (info) => info.getValue() ?? '-',
      },
      {
        id: 'order_quantity',
        accessorKey: 'order_quantity',
        header: 'Order Qty',
        size: 100,
        minSize: 80,
        cell: (info) => info.getValue() ?? '-',
      },
      {
        id: 'text_a',
        accessorKey: 'text_a',
        header: 'Text A',
        size: 120,
        minSize: 100,
        cell: (info) => info.getValue() ?? '-',
      },
      {
        id: 'text_b',
        accessorKey: 'text_b',
        header: 'Text B',
        size: 120,
        minSize: 100,
        cell: (info) => info.getValue() ?? '-',
      },
      {
        id: 'text_c',
        accessorKey: 'text_c',
        header: 'Text C',
        size: 100,
        minSize: 80,
        cell: (info) => info.getValue() ?? '-',
      },
      {
        id: 'text_tt',
        accessorKey: 'text_tt',
        header: 'Text TT',
        size: 120,
        minSize: 100,
        cell: (info) => info.getValue() ?? '-',
      },
      {
        id: 'specified_customer_id',
        accessorKey: 'specified_customer_id',
        accessorFn: (row) => row.metadata?.SpecifiedCustomer?.long_name,
        header: 'Customer',
        size: 100,
        minSize: 80,
      },
      {
        id: 'limit_price',
        accessorKey: 'limit_price',
        accessorFn: (row) => row.limit_price,
        header: 'Limit Price',
        size: 100,
        minSize: 80,
      },
      {
        id: 'working_quantity',
        accessorKey: 'working_quantity',
        header: 'Working Qty',
        size: 100,
        minSize: 80,
      },
      {
        id: 'source',
        accessorKey: 'source',
        header: 'Source',
        size: 100,
        minSize: 80,
        cell: (info) => {
          const value = info.getValue() as string;
          return (
            <Badge variant="outline" className="text-xs capitalize">
              {value}
            </Badge>
          );
        },
      },
      {
        id: 'exchange_event_time',
        accessorKey: 'exchange_event_time',
        header: 'Time',
        size: 100,
        minSize: 80,
        cell: (info) => {
          const value = info.getValue() as string;
          return (
            moment.tz(value, CommonDestinationTimezone).format("MM/DD/YYYY hh:mm a")
          );
        },
      },
    ],
    []
  );

  const defaultColumnVisibility = useMemo(
    () => ({
      trading_account_name: true,
      instrument_name: true,
      order_quantity: true,
      text_a: true,
      text_b: true,
      text_c: true,
      text_tt: true,
      source: true,
      actions: true,
      exchange_event_time: false,
    }),
    []
  );

  // Fill events table columns
  const fillEventsColumns: DataTableColumn<TtFillEventRow>[] = useMemo(
    () => [
      {
        id: 'customer_id',
        accessorKey: 'metadata.specified_customer_id',
        accessorFn: (row) => row.metadata?.SpecifiedCustomer?.long_name,
        header: 'Customer',
        minSize: 100,
      },
      {
        id: 'exchange_event_time',
        accessorKey: 'exchange_event_time',
        header: 'Exchange Event Time',
        size: 180,
        minSize: 150,
        cell: (info) => {
          const value = info.getValue() as string | null;
          return value
            ? moment.tz(value, CommonDestinationTimezone).format('MM/DD/YYYY hh:mm:ss a')
            : '-';
        },
      },
      {
        id: 'buy_sell_type',
        accessorKey: 'buy_sell_type',
        header: 'Buy/Sell',
        size: 100,
        minSize: 80,
        cell: (info) => info.getValue() ?? '-',
      },
      {
        id: 'order_quantity',
        accessorKey: 'order_quantity',
        header: 'Order Qty',
        size: 100,
        minSize: 80,
        cell: (info) => info.getValue() ?? '-',
      },
      {
        id: 'fill_quantity',
        accessorKey: 'fill_quantity',
        header: 'Fill Qty',
        size: 100,
        minSize: 80,
        cell: (info) => info.getValue() ?? '-',
      },
      {
        id: 'avg_fill_price',
        accessorKey: 'avg_fill_price',
        header: 'Avg Fill Price',
        size: 120,
        minSize: 100,
        cell: (info) => info.getValue() ?? '-',
      },
      {
        id: 'message',
        accessorKey: 'message',
        header: 'Message',
        size: 200,
        minSize: 150,
        enableTooltip: true,
        cell: (info) => info.getValue() ?? '-',
      },
      {
        id: 'instrument_name',
        accessorKey: 'instrument_name',
        header: 'Instrument',
        size: 200,
        minSize: 150,
        cell: (info) => info.getValue() ?? '-',
      },
      {
        id: 'text_a',
        accessorKey: 'text_a',
        header: 'Text A',
        size: 120,
        minSize: 100,
        cell: (info) => info.getValue() ?? '-',
      },
      {
        id: 'text_b',
        accessorKey: 'text_b',
        header: 'Text B',
        size: 120,
        minSize: 100,
        cell: (info) => info.getValue() ?? '-',
      },
      {
        id: 'text_c',
        accessorKey: 'text_c',
        header: 'Text C',
        size: 100,
        minSize: 80,
        cell: (info) => info.getValue() ?? '-',
      },
      {
        id: 'text_tt',
        accessorKey: 'text_tt',
        header: 'Text TT',
        size: 120,
        minSize: 100,
        cell: (info) => info.getValue() ?? '-',
      },
    ],
    []
  );

  const handleFillEventRowClick = (fillEvent: TtFillEventRow) => {
    navigate(
      RoutePath.TTOrdersDetail.replace(':guid_order_id', encodeURIComponent(fillEvent.guid_order_id))
    );
  };

  return (
    <div className="space-y-4">
      {/* View Mode Toggle */}
      <div className="flex gap-2 border-b pb-2">
        <Badge
          variant={viewMode === 'orders' ? 'default' : 'outline'}
          className="cursor-pointer transition-colors"
          onClick={() => setViewMode('orders')}
        >
          Orders View
        </Badge>
      </div>

      {viewMode === 'orders' && (
        <>
          {/* Top-Level Filter Buttons for Orders */}
          <div className="flex gap-2 flex-wrap">
            <Badge
              variant={activeFilter === 'missing_applicable' ? 'default' : 'outline'}
              className={cn(
                'cursor-pointer transition-colors',
                activeFilter === 'missing_applicable' && 'bg-orange-600'
              )}
              onClick={() => onFilterChange('missing_applicable')}
            >
              Missing Customer ID
            </Badge>
          </div>

          {/* Orders Data Table */}
          {isLoading ? (
            <div className="p-4 border rounded-md">
              <p>Loading orders...</p>
            </div>
          ) : (
            <DataTable
              data={filteredOrders}
              columns={columns}
              enableSorting
              enableMultiSort
              enableFiltering
              enableColumnVisibility
              enableColumnPinning
              enableExport
              defaultColumnVisibility={defaultColumnVisibility}
              defaultSorting={[{ id: "exchange_event_time", desc: true }]}
              emptyMessage="No orders found for this filter"
              stickyHeaders
              tableClassName="max-h-[calc(100vh-500px)] overflow-y-scroll"
              rowClickable
              onRowClick={handleRowClick}
            />
          )}
        </>
      )}

      {/* Attach Customer Modal */}
      {selectedGuidOrderId && (() => {
        const selectedOrder = filteredOrders.find(order => order.guid_order_id === selectedGuidOrderId);
        return (
          <AttachCustomerModal
            open={modalOpen}
            onOpenChange={setModalOpen}
            guidOrderId={selectedGuidOrderId}
            existingMetadata={selectedOrder?.metadata}
            orderTextFields={{
              textA: selectedOrder?.text_a,
              textB: selectedOrder?.text_b,
              textC: selectedOrder?.text_c,
              textTT: selectedOrder?.text_tt,
            }}
          />
        );
      })()}
    </div>
  );
}
